import React, {useEffect, useMemo, useState} from 'react';
import {I18n} from 'react-redux-i18n';

import {useDispatch, useSelector} from 'react-redux';
import {WithStyles, withStyles, Table, TableHead,
  TableRow, TableCell, TableBody} from '@material-ui/core';
import {Dropdown} from '../../components';
import {CustomInput} from '../../material-dashboard-pro-react/components';

import {DATA_EXPORT_MAPPINGS_REDUCER} from './slice';
import {fetchDefaultMapping} from './action';
import {DATA_EXPORT_REDUCER} from '../DataExportEditForm/slice';
import {
  DataExportModel,
  DataExportMapping,
  DataExportBuiltInMappingInfo,
  DataExportType,
} from '../../types/DataExport';

import style from './style';

type DataExportFieldsTabProps = WithStyles<typeof style> & {
  // eslint-disable-next-line no-unused-vars
  onFieldChange: (key: keyof DataExportModel, value: any) => void;
  dataExport: DataExportModel;
  isEditMode: boolean;
};

function DataExportFieldsTab({
  classes,
  onFieldChange,
  dataExport,
  isEditMode,
}: DataExportFieldsTabProps) {
  const dispatch = useDispatch();

  const [fieldsMapping, setFieldsMapping] = useState<DataExportMapping>({});

  const dataExportTypes: DataExportType[] = useSelector((store: any) =>
    store[DATA_EXPORT_REDUCER].dataExportTypes || []);
  const builtInMappings = useSelector((store: any) =>
    store[DATA_EXPORT_MAPPINGS_REDUCER].defaultMappings as DataExportBuiltInMappingInfo[] || {});

  const mappingOptions = useMemo(() => {
    const mappings = builtInMappings.map((mapping) => ({
      label: mapping.name,
      value: mapping.id,
    }));

    const selectedCollectorType
      = dataExportTypes.find((type) => type.id === dataExport.streamTypeId);
    if (selectedCollectorType?.isCustomFieldMappingAllowed) {
      mappings.push({
        label: I18n.t('dataExportPage.editForm.fields.dropdownlabels.custom'),
        value: 'custom',
      });
    }

    return mappings;
  }, [builtInMappings, dataExport?.streamTypeId]);

  useEffect(() => {
    fetchDefaultMapping(dispatch);
  }, []);

  useEffect(() => {
    if (dataExport.builtinFieldMappingId ||
      !dataExport.customFieldMapping ||
      Object.keys(dataExport.customFieldMapping).length === 0
    ) {
      setFieldsMapping(
        builtInMappings.length > 0 ?
          builtInMappings.find((mapping) =>
            mapping.id === dataExport.builtinFieldMappingId)?.mapping ||
            builtInMappings[0].mapping :
          {},
      );
      return;
    }

    const selectedCollectorType = dataExportTypes
      .find((type) => type.id === dataExport.streamTypeId);
    let defaultIndex = builtInMappings
      .findIndex((m) => m.id === selectedCollectorType?.defaultFieldMappingId);
    if (defaultIndex < 0) {
      defaultIndex = 0;
    }

    if (builtInMappings.length === 0 ||
      Object.keys(builtInMappings[defaultIndex].mapping)
        .every((m) => Object.keys(dataExport.customFieldMapping).includes(m))
    ) {
      setFieldsMapping(dataExport.customFieldMapping);
    } else {
      const mapping: {[key: string]: string} = {};
      Object.entries(builtInMappings[defaultIndex].mapping).forEach(([key, value]) => {
        mapping[key] = dataExport.customFieldMapping[key] ?? value ?? key;
      });
      setFieldsMapping(mapping);
    }
  }, [dataExport.builtinFieldMappingId, builtInMappings, dataExport.customFieldMapping]);

  useEffect(() => {
    if (!dataExport.builtinFieldMappingId &&
        fieldsMapping && Object.keys(fieldsMapping).length > 0
    ) {
      onFieldChange('customFieldMapping', fieldsMapping);
    }
  }, [fieldsMapping]);

  const handleFieldsMappingChange = (key: string, value: string) => {
    setFieldsMapping({
      ...fieldsMapping,
      [key]: value,
    });
  };

  const handleFieldsMappingTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const mappingId = mappingOptions[Number(e.target.value)].value;
    onFieldChange('builtinFieldMappingId', mappingId !== 'custom' ? mappingId : null);
    handleFieldsMappingChange('', '');
  };

  return (
    <>
      <h3>{I18n.t('dataExportPage.editForm.fields.headers.baseFields')}</h3>
      <div>
        <Dropdown
            customSelectClass="dropdown__select_type"
            disabled={
              !isEditMode || dataExportTypes
                .find((type) => type.id === dataExport.streamTypeId)?.useDefaultFieldMappingOnly
            }
            fullWidth={true}
            label={
              I18n.t('dataExportPage.editForm.fields.labels.builtinMappings')
            }
            onChangeValue={handleFieldsMappingTypeChange}
            options={mappingOptions}
            selectedItemIndex={
              dataExport.builtinFieldMappingId ?
                mappingOptions.findIndex((option) =>
                  option.value === dataExport.builtinFieldMappingId) :
                (mappingOptions.length - 1)
            }
        />
        <Table>
          <TableHead>
            <TableRow className={classes.fields__tableHead}>
              <TableCell className={classes.fields__enabledText}>
                Default Field
              </TableCell>
              <TableCell className={classes.fields__enabledText}>
                Export Field
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              Object.entries(fieldsMapping).map(([key, value]) => (
                <TableRow
                  key={key}
                >
                  <TableCell
                    className={
                      !value || (value && value.trim().length <= 0) ?
                        classes.fields__disabledText :
                        classes.fields__enabledText
                    }
                  >
                    {key}
                  </TableCell>
                  <TableCell
                    className={classes.fields__enabledText}
                  >
                    <CustomInput
                      inputProps={{
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                          handleFieldsMappingChange(key, e.target.value),
                        type: 'text',
                        value: value,
                        disabled: !isEditMode || dataExport.builtinFieldMappingId,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export default withStyles(style)(DataExportFieldsTab);
